import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, Input, input, OnInit, ViewChild } from '@angular/core';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { Event, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';

import { asyncScheduler, combineLatest, filter, map, observeOn, of, scan, startWith, take } from 'rxjs';

import { AdobeAnalyticsService } from 'src/app/modules/core/services/adobe-analytics.service';
import { AuthService } from 'src/app/modules/core/services/auth.service';
import { BrandService } from 'src/app/modules/core/services/brand.service';
import { RealtorActionsService } from 'src/app/modules/core/services/realtor-actions.service';
import { ScrollService } from 'src/app/modules/core/services/scroll.service';
import { SettingsService } from 'src/app/modules/core/services/settings.service';
import { SpinnerService } from 'src/app/modules/core/services/spinner.service';
import { FeedbackActionsService } from 'src/app/modules/core/services/feedback-actions.service';
import { PostCloseDateService } from 'src/app/modules/core/services/post-close-date.service';

import { AppFacade } from 'src/app/modules/ngrx-store/app/app.facade';
import { ConstructionProgressFacade } from 'src/app/modules/ngrx-store/construction-progress/construction-progress.facade';
import { ServiceRequestsFacade } from 'src/app/modules/ngrx-store/service-requests/service-requests.facade';
import { YourHomeFacade } from 'src/app/modules/ngrx-store/your-home/your-home.facade';
import { MessageFacade } from 'src/app/modules/ngrx-store/message/message.facade';
import { RealtorFacade } from 'src/app/modules/ngrx-store/realtor/realtor.facade';

import { Breakpoints } from 'src/app/modules/shared/types/breakpoints';
import { UnsubscribeOnDestroy } from 'src/app/modules/shared/utilities/unsubscribe-on-destroy';
import { InternalUrlPipe } from 'src/app/modules/shared/pipes/internal-url.pipe';
import { CORE_ROUTES, NAVIGATION_ROUTES, REALTOR_ROUTES } from 'src/app/modules/shared/constants/route-paths';
import { InteractionContext } from 'src/app/modules/shared/constants/adobe-analytics-fields';
import { getCtaClickProps, getPageLoadEvars } from 'src/app/modules/shared/utilities/adobe-analytics.utils';
import { getPageNameFromUrl } from 'src/app/modules/shared/utilities/page-name.utils';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { HeaderComponent } from 'src/app/modules/core/components/header/header.component';
import { NavigationComponent } from 'src/app/modules/core/components/navigation/navigation.component';
import { FooterComponent } from 'src/app/modules/core/components/footer/footer.component';
import { IfViewSizeDirective } from 'src/app/modules/shared/directives/if-view-size.directive';
import { SpinnerComponent } from 'src/app/modules/shared/components/spinner/spinner.component';
import { keyPressHandler } from 'src/app/modules/shared/utilities/accessibility.utils';

interface ScrollPositionRestore {
	event: Event;
	positions: { [K: number]: number };
	trigger: 'imperative' | 'popstate' | 'hashchange';
	idToRestore: number;
}

@Component({
	selector: 'cp-customer-portal',
	templateUrl: './customer-portal.component.html',
	styleUrls: ['./customer-portal.component.scss'],
	imports: [MatSidenavContainer, MatSidenav, IfViewSizeDirective, SpinnerComponent, MatIconButton, MatIcon, MatButton, MatSidenavContent, HeaderComponent, NavigationComponent, RouterOutlet, FooterComponent, AsyncPipe],
	providers: [InternalUrlPipe]
})
export class CustomerPortalComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewChecked  {
	@Input() salesAgreementId?: number;
	isPreviewRealtor = input<boolean | undefined>();

	@ViewChild('sidenav') sidenav?: MatSidenav;
	@ViewChild('cpNavContainer') cpNavContainer?: ElementRef;
	@ViewChild('cpMainContent') cpMainContent?: ElementRef;

	homeDetails$ = this.yourHomeFacade.homeDetails$;

	homeDetailsPending$ = this.yourHomeFacade.homeDetailsPending$;

	userInfo$ = this.appFacade.userInfo$;

	realtorInfo$ = this.yourHomeFacade.realtorInfo$;

	myAccountUrl: string | undefined;

	spinnerActive$ = this.spinnerService.spinnerActive$;

	scrollContainerSet = false;

	isPreview: boolean = false;

	isRealtorEnabled: boolean = false;

	isRealtor: boolean = false;

	isThirtyOneDaysPostClose: boolean = false;

	showNavigation: boolean = true;

	isOnRealtorHomesPage: boolean = false;

	keyPressHandler = keyPressHandler;

	primaryOwnerName$ = this.authService.isRealtorUser() 
		? combineLatest([
			this.realtorFacade.realtorGrants$,
			this.yourHomeFacade.salesAgreementId$
		]).pipe(
			map(([grants, salesAgreementId]) => 
				grants?.find(g => g.salesAgreementId === salesAgreementId)?.ownersName
			)
		)
		: of(undefined);

	constructor(
		private adobeAnalyticsService: AdobeAnalyticsService,
		private authService: AuthService,
		public scrollService: ScrollService,
		public yourHomeFacade: YourHomeFacade,
		private breakpointObserver: BreakpointObserver,
		private router: Router,
		private appFacade: AppFacade,
		private brandService: BrandService,
		private settingsService: SettingsService,
		private constructionProgressFacade: ConstructionProgressFacade,
		private realtorFacade: RealtorFacade,
		private serviceRequestFacade: ServiceRequestsFacade,
		private internalUrlPipe: InternalUrlPipe,
		private realtorActionsService: RealtorActionsService,
		private spinnerService: SpinnerService,
		private feedbackActionsService: FeedbackActionsService,
		private messageFacade: MessageFacade,
		private postCloseDateService: PostCloseDateService
	) {
		super();
		// Scroll to top on emit - For when the user clicks the SCAR progress icon on the construction progress page
		this.scrollService.scrollToTop$.subscribe(() => {
			if (this.cpNavContainer) {
				this.cpNavContainer.nativeElement.scrollTop = 0;
			}

			if (this.cpMainContent) {
				this.cpMainContent.nativeElement.scrollTop = 0;
			}
		});

		this.settingsService.getSettings().pipe(
			this.takeUntilDestroyed()
		).subscribe(settings => {
			const brand = this.brandService.getBrand();

			this.myAccountUrl = settings.brandConfigs[brand.name].myAccountUrl;
		});
	}

	ngOnInit(): void {
		this.isPreview = this.authService.isPreviewUser();
		this.isRealtor = this.authService.isRealtorUser();
		this.redirectInvalidPreviewDeepLink();

		// Hide the navigation sidebar when the realtor is on the home list (CM) page
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(event => (event as NavigationEnd).urlAfterRedirects),
			startWith(this.router.url),
			this.takeUntilDestroyed()
		).subscribe(url => {
			const realtorHomesPath = `/${NAVIGATION_ROUTES.Realtor}/${REALTOR_ROUTES.Homes}`;
			this.isOnRealtorHomesPage = this.isRealtor && url.startsWith(realtorHomesPath);
			this.showNavigation = !this.isOnRealtorHomesPage;
		});

		this.breakpointObserver.observe(
			Breakpoints.desktop
		).pipe(this.takeUntilDestroyed())
			.subscribe((result: BreakpointState) => {
				if(result.breakpoints[Breakpoints.desktop]) {
				// Edge case handler for when you open sidenav in tablet and resize the browser to desktop, and the dark overlay exists
					this.sidenav?.close();
				}
			});

		// Uses the router events to determine if the scroll bar should be set at the top or a prior position
		this.router.events.pipe(
			filter(
				(event: Event) => event instanceof NavigationStart || event instanceof NavigationEnd,
			),
			// Scan acts as a reducer over time, storing all of the navigation events so that we can access
			// the scroll positions as necessary
			scan<Event, ScrollPositionRestore>((acc, event) => ({
				event,
				positions: {
					...acc.positions,
					...(event instanceof NavigationStart
						? { [event.id]: this.cpNavContainer?.nativeElement.scrollTop }
						: {}
					),
					...acc.positions,
					...(event instanceof NavigationStart
						? { [event.id]: this.cpMainContent?.nativeElement.scrollTop }
						: {}
					)
				},
				trigger:
					event instanceof NavigationStart && event.navigationTrigger
						? event.navigationTrigger
						: acc.trigger,
				idToRestore:
					(event instanceof NavigationStart && event.restoredState &&
						event.restoredState.navigationId + 1) || acc.idToRestore,
			}), {} as ScrollPositionRestore),
			filter(
				// Filter out events that contain a url fragment so the fragment directive can handle the scrolling
				({ event, trigger }) => event instanceof NavigationEnd && !!trigger && !event.url.includes('#'),
			),
			observeOn(asyncScheduler),
		).pipe(
			this.takeUntilDestroyed()
		).subscribe(({ trigger, positions, idToRestore }) => {
			if (this.cpMainContent && this.cpNavContainer) {
				if (trigger !== 'hashchange') {
					if (trigger === 'imperative') {
						this.cpNavContainer.nativeElement.scrollTop = 0;
						this.cpMainContent.nativeElement.scrollTop = 0;
					}
					
					if (trigger === 'popstate') {
						this.cpNavContainer.nativeElement.scrollTop = positions[idToRestore];
						this.cpMainContent.nativeElement.scrollTop = positions[idToRestore];
					}
				}
			}
		});

		// Keep isThirtyOneDaysPostClose updated whenever homeDetails changes, settings required by PostCloseDateService
		combineLatest([
			this.yourHomeFacade.homeDetails$,
			this.settingsService.getSettings()
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe(([homeDetails]) => {
			const closeOfEscrow = homeDetails?.lot.closeOfEscrow;
			this.isThirtyOneDaysPostClose = closeOfEscrow ? this.postCloseDateService.getIsThirtyOneDaysSince(closeOfEscrow) : false;
		});

		// One-shot initialization: load data or handle SAG mismatch on first emission
		this.yourHomeFacade.homeDetails$.pipe(
			take(1)
		).subscribe((homeDetails) => {
			// If browser storage does not have home details, or if the SAG ID doesn't match, fetch the details from API
			if (this.salesAgreementId && homeDetails?.salesAgreementId !== +this.salesAgreementId) {
				// Reset data to ensure nothing lingers during loading of new home details
				this.constructionProgressFacade.resetConstructionUpdates();
				this.serviceRequestFacade.resetServiceRequests();

				const transformedUrl = this.internalUrlPipe.transform(NAVIGATION_ROUTES.Dashboard);

				this.router.navigate([transformedUrl], { queryParamsHandling: 'preserve' }).then(() => {
					this.yourHomeFacade.loadHomeDetails(this.salesAgreementId!);
				});
			} else {
				// If this is the same agreement, load new construction updates and service requests
				// Corresponding effects will prevent unnecessary API call based on current home phase
				this.constructionProgressFacade.loadConstructionUpdates();
				this.serviceRequestFacade.loadServiceRequests();
				this.messageFacade.loadMessageThreads();
			}
		});

		// Keep isRealtorEnabled in sync for all users
		this.yourHomeFacade.realtorEnabled$.pipe(
			this.takeUntilDestroyed()
		).subscribe(realtorEnabled => {
			this.isRealtorEnabled = realtorEnabled;
		});
	}

	private redirectInvalidPreviewDeepLink(): void {
		if (!this.isPreview || !this.isPreviewRealtor()) {
			return;
		}

		const previewRealtorBase = `/${CORE_ROUTES.Preview}/${NAVIGATION_ROUTES.Realtor}`;
		const currentUrl = this.router.url || '';

		if (!currentUrl.includes(`/${CORE_ROUTES.Preview}/`)) {
			return;
		}

		if (!currentUrl.startsWith(previewRealtorBase)) {
			this.router.navigateByUrl(previewRealtorBase, { replaceUrl: true });
			return;
		}

		if (currentUrl === previewRealtorBase) {
			return;
		}

		this.realtorFacade.realtorGrants$.pipe(
			take(1),
			map((grants) => Boolean(grants?.length))
		).subscribe((hasGrants) => {
			if (!hasGrants) {
				this.router.navigateByUrl(previewRealtorBase, { replaceUrl: true });
			}
		});
	}

	ngAfterViewChecked(): void {
		if (!this.scrollContainerSet && (this.cpNavContainer)) {
			this.scrollService.scrollContainer = this.cpNavContainer?.nativeElement;
			this.scrollContainerSet = true;
		}
	}
	
	onMyAccountClick(): void {
		// Track adobe CTA click for My Account
		const pageName = getPageNameFromUrl(this.currentUrl);

		const evar = getPageLoadEvars(pageName);
		const prop = getCtaClickProps(InteractionContext.Navigating, 'Header', 'My Account');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
	}

	onRealtorAccessClick(): void {
		// Close the sidenav before navigating
		this.sidenav?.close();
		this.realtorActionsService.navigateToRealtorAccess();
	}

	onLogout(): void {
		if (!this.isPreview) {
			this.authService.logout();
		}
	}

	onCloseClick(): void {
		this.sidenav?.close();

		// Track adobe CTA click for Close Sidenav
		const pageName = getPageNameFromUrl(this.currentUrl);

		const evar = getPageLoadEvars(pageName);
		const prop = getCtaClickProps(InteractionContext.Viewing, 'Header', 'Close');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
	}

	onResetPassword(): void {
		this.realtorActionsService.initiatePasswordReset();
	}

	onProfileManagement(): void {
		this.realtorActionsService.initiateProfileManagement();
	}

	onGiveFeedbackClick(): void {
		this.sidenav?.close();

		const pageName = getPageNameFromUrl(this.currentUrl);
		const evar = getPageLoadEvars(pageName);
		const prop = getCtaClickProps(InteractionContext.Navigating, 'Header', 'Give Feedback');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);

		this.yourHomeFacade.homeDetails$.pipe(take(1)).subscribe(homeDetails => {
			this.feedbackActionsService.openFeedbackModal(homeDetails ?? null);
		});
	}

	get currentUrl(): string {
		return this.router.url;
	}
}